/**
 * recommendation-engine-v2.ts
 * Layer 1: Deterministic Engine — harte Aussagen, Zahlen, Prioritäten
 */

import type { MasterResult } from "@/lib/master-engine";

function formatCurrency(n: number): string {
  return `${Math.round(n).toLocaleString("de-DE")} €`;
}

export type SalesNarrative = {
  hero: {
    headline: string;
    subline: string;
    urgency: string;
    severity: "critical" | "warning" | "ok";
  };
  rootCause: string;
  rootCauseDetail: string;
  quickWins: Array<{
    title: string;
    description: string;
    timeframe: string;
    owner: string;
  }>;
  businessImpact: {
    inefficiency: number;
    inefficiencyFormatted: string;
    revenuePerEuroSalary: number;
    message: string;
    context: string;
  };
  priorities: string[];
  oneThing: string;
  risk: string;
  capacityMessage: string | null;
  salesCycleWarning: string | null;
};

export function buildSalesNarrative(result: MasterResult): SalesNarrative | null {
  if (!result || !result.teamPerformance) return null;

  const t = result.teamPerformance;
  const m = result.computedMetrics;

  const target = t.totalRevenueTargetYear || 0;
  const forecast = t.projectedYearEndRevenue || 0;
  const gap = t.projectedYearEndGap || 0;
  // Monatlicher Verlust: Pace-Gap / 12 hat Priorität, dann Funnel-Gap
  const monthlyGap =
    gap > 0
      ? Math.round(gap / 12)
      : t.pacingRecoveryPotentialMonthly || m?.monthlyRevenueGap || 0;

  const pacingProblem = !!t.summarySignals?.hasPacingProblem;
  const pipelineProblem = !!t.summarySignals?.hasPipelineProblem;
  const targetProblem = !!t.summarySignals?.hasTargetBreakdownProblem;
  const varianceProblem = !!t.summarySignals?.hasVarianceProblem;
  const reviewProblem = !!t.summarySignals?.hasReviewProblem;

  // ── SEVERITY ────────────────────────────────────────────────────
  const severity: "critical" | "warning" | "ok" =
    gap > target * 0.2
      ? "critical"
      : gap > target * 0.05
      ? "warning"
      : "ok";

  // ── HERO ────────────────────────────────────────────────────────
  const hero = {
    headline: gap > 0
      ? `Ihr Team steuert auf ${formatCurrency(forecast)} statt ${formatCurrency(target)} zu.`
      : `Ihr Team ist auf Kurs — das Jahresziel ist erreichbar.`,
    subline: gap > 0
      ? `Das entspricht einer Ziellücke von ${formatCurrency(gap)}.`
      : `Weiter so — die Struktur stimmt.`,
    urgency: monthlyGap > 0
      ? `Aktuell bleiben monatlich rund ${formatCurrency(monthlyGap)} Umsatz ungenutzt.`
      : `Fokus auf Qualität und Skalierung.`,
    severity,
  };

  // ── ROOT CAUSE ──────────────────────────────────────────────────
  let rootCause = "";
  let rootCauseDetail = "";
  if (pipelineProblem) {
    rootCause = "Unzureichende Pipeline-Abdeckung";
    rootCauseDetail = "Ein Großteil der Reps arbeitet nicht mit genug Pipeline zur Zielerreichung. Das ist ein strukturelles Risiko — kein Motivationsproblem.";
  } else if (targetProblem) {
    rootCause = "Fehlende Zielsteuerung auf Rep-Ebene";
    rootCauseDetail = "Das Jahresziel ist nicht konsequent auf Reps heruntergebrochen. Ohne klare Zielverantwortung fehlt die Steuerungsgrundlage.";
  } else if (varianceProblem) {
    rootCause = "Hohe Leistungsstreuung im Team";
    rootCauseDetail = "Die Leistungsunterschiede zwischen Top- und Durchschnitts-Reps sind zu hoch. Ursache ist meist fehlende Standardisierung, nicht mangelndes Talent.";
  } else if (pacingProblem) {
    rootCause = "Team liegt unter dem linearen Sollverlauf";
    rootCauseDetail = "Das Team liegt aktuell hinter dem erwarteten Jahres-Pace. Ohne Gegensteuerung summiert sich das zur Ziellücke.";
  } else if (reviewProblem) {
    rootCause = "Fehlende Steuerungsdisziplin und Review-Rhythmus";
    rootCauseDetail = "Ohne regelmäßige Performance-Reviews fehlen Sichtbarkeit, Korrekturmechanismen und Verbindlichkeit.";
  } else {
    rootCause = "Strukturelle Sales-Leckagen im Funnel";
    rootCauseDetail = "Die aktuelle Sales-Struktur verhindert planbare, stabile Ergebnisse. Die Ursachen liegen meist in Prozess, Ownership und Qualifizierung.";
  }

  // ── QUICK WINS ──────────────────────────────────────────────────
  const quickWins: SalesNarrative["quickWins"] = [];

  if (pacingProblem) {
    quickWins.push({
      title: "Sales-Pace sofort sichtbar machen",
      description: "Wöchentliches Soll-Ist-Tracking pro Rep einführen — jeder kennt seinen Stand.",
      timeframe: "3 Tage",
      owner: "Head of Sales",
    });
  }
  if (pipelineProblem) {
    quickWins.push({
      title: "Pipeline-Lücken identifizieren",
      description: "Pipeline-Coverage pro Rep berechnen: Hat jeder Rep genug Pipeline für sein Quartalsziel?",
      timeframe: "7 Tage",
      owner: "Sales Lead",
    });
  }
  if (varianceProblem) {
    quickWins.push({
      title: "Top-Performer analysieren",
      description: "Was machen die besten 20% anders? Discovery, Follow-up, Closing-Trigger dokumentieren.",
      timeframe: "7 Tage",
      owner: "Head of Sales",
    });
  }
  if (targetProblem) {
    quickWins.push({
      title: "Ziele auf Rep-Ebene herunterbrechen",
      description: "Jahresziel in monatliche Rep-Ziele übersetzen und verbindlich kommunizieren.",
      timeframe: "48 Stunden",
      owner: "Geschäftsführung",
    });
  }
  if (reviewProblem) {
    quickWins.push({
      title: "Wöchentliche Pipeline-Reviews einführen",
      description: "Jeden Montag: Welche Deals schließen diese Woche? Was blockiert? Was braucht Entscheidung?",
      timeframe: "Sofort starten",
      owner: "Sales Lead",
    });
  }
  // Auffüllen bis 3
  if (quickWins.length < 3) {
    quickWins.push({
      title: "Deals reaktivieren",
      description: "Alle Deals ohne Fortschritt seit >14 Tagen priorisieren und mit konkretem Next Step versehen.",
      timeframe: "48 Stunden",
      owner: "Jeder Rep",
    });
  }
  if (quickWins.length < 3) {
    quickWins.push({
      title: "Funnel-Leckage lokalisieren",
      description: "Welche Conversion-Stufe hat den größten Abbruch? Dort sofort Ownership und Standard definieren.",
      timeframe: "5 Tage",
      owner: "Head of Sales",
    });
  }

  // ── BUSINESS IMPACT ─────────────────────────────────────────────
  const reps = t.productiveReps || 1;
  const costEfficiency = m?.costInefficiency;
  const inefficiency = Math.round(costEfficiency?.inefficientSalaryCostYear || 0);
  const revenuePerEuroSalary =
    Math.round((costEfficiency?.revenuePerEuroSalary || 0) * 10) / 10;

  const businessImpact: SalesNarrative["businessImpact"] = {
    inefficiency,
    inefficiencyFormatted: formatCurrency(inefficiency),
    revenuePerEuroSalary,
    message:
      costEfficiency?.showBudgetEfficiencyWarning && inefficiency > 5000
        ? `Ein Teil des Sales-Budgets wird aktuell noch nicht voll wirksam in Zielerreichung übersetzt.`
        : `Die Budgeteffizienz wirkt aktuell nicht wie das größte Akutproblem.`,
    context:
      revenuePerEuroSalary < 3
        ? `Pro Euro Gehaltskosten generiert Ihr Team aktuell ${revenuePerEuroSalary} € Umsatz — klar unter einem belastbaren Niveau.`
        : revenuePerEuroSalary < 5
        ? `Pro Euro Gehaltskosten generiert Ihr Team aktuell ${revenuePerEuroSalary} € Umsatz — ausbaufähig.`
        : revenuePerEuroSalary < 7
        ? `Pro Euro Gehaltskosten generiert Ihr Team aktuell ${revenuePerEuroSalary} € Umsatz — solide, aber noch nicht stark skaliert.`
        : `Pro Euro Gehaltskosten generiert Ihr Team aktuell ${revenuePerEuroSalary} € Umsatz — ein starkes Verhältnis.`,
  };

  // ── PRIORITIES ──────────────────────────────────────────────────
  const priorities: string[] = [];

  // Goal Path als erste Priorität einbinden (wenn Daten vorhanden)
  if (m?.goalPath?.requiredContactsPerMonthFromNow && m.goalPath.requiredContactsPerMonthFromNow > 0) {
    priorities.unshift(
      `Zielpfad absichern: ${Math.round(m.goalPath.requiredContactsPerMonthFromNow)} Kontakte/Monat und ${m.goalPath.requiredDealsPerMonthFromNow?.toFixed(1)} Deals/Monat ermöglichen`
    );
  }

  if (pacingProblem) priorities.push("Sales Output kurzfristig erhöhen");
  if (targetProblem) priorities.push("Zielverantwortung pro Rep herstellen");
  if (pipelineProblem) priorities.push("Pipeline-Abdeckung pro Rep sichern");
  if (varianceProblem) priorities.push("Top-Performer-Ansätze skalieren");
  if (reviewProblem) priorities.push("Wöchentlichen Review-Rhythmus verankern");

  if (priorities.length === 0) {
    priorities.push("Funnel-Conversion auf Benchmark-Niveau bringen");
    priorities.push("Lead-Ownership und Reaktionsstandards definieren");
    priorities.push("Discovery-Qualität systematisch verbessern");
  }

  // ── ONE THING ───────────────────────────────────────────────────
  let oneThing = "Führen Sie ein wöchentliches Performance-Review pro Rep ein.";
  if (pacingProblem)
    oneThing = `Führen Sie ein wöchentliches Pace-Review ein: Jeder Rep kennt seinen Soll-Stand (${formatCurrency(t.targetPerRepMonth)}/Monat) und seinen aktuellen Gap.`;
  else if (targetProblem)
    oneThing = "Brechen Sie das Jahresziel heute auf monatliche Rep-Ziele herunter und kommunizieren Sie es verbindlich.";
  else if (pipelineProblem)
    oneThing = "Prüfen Sie die Pipeline-Coverage pro Rep: Hat jeder Rep genug valide Chancen für sein Quartalsziel?";
  else if (varianceProblem)
    oneThing = "Analysieren Sie Ihre Top-Performer und überführen Sie deren Muster in ein verbindliches Team-Standard.";

  // ── RISK ────────────────────────────────────────────────────────
  const risk =
    gap > 0
      ? `Ohne Anpassung wird Ihr Team das Jahresziel von ${formatCurrency(target)} voraussichtlich um ${formatCurrency(gap)} verfehlen. Das entspricht ${formatCurrency(gap / 12)} verlorenen Umsatz pro Monat.`
      : `Das Jahresziel ist erreichbar. Fokus auf Execution und Konsistenz.`;

  // ── CAPACITY ────────────────────────────────────────────────────
  let capacityMessage: string | null = null;
  if (m?.capacityCheck?.isTargetUnreachableWithCurrentSetup) {
    capacityMessage = `Kapazitätsprüfung: Selbst bei realistischer Optimierung liegt die theoretische Maximalkapazität bei ${formatCurrency(
      m.capacityCheck.optimizedMaxRevenueCapacity || m.capacityCheck.maxRevenueCapacity || 0
    )}. Das Ziel liegt bei ${formatCurrency(target)}. Das ist weniger ein Execution-Thema als ein strukturelles Kapazitätsthema.`;
  } else if (m?.capacityCheck?.isTargetUnreachableWithCurrentPerformance) {
    capacityMessage = `Kapazitätsprüfung: Mit aktueller Performance liegt die theoretische Maximalkapazität bei ${formatCurrency(
      m.capacityCheck.currentMaxRevenueCapacity || 0
    )}, mit realistischer Optimierung bei ${formatCurrency(
      m.capacityCheck.optimizedMaxRevenueCapacity || 0
    )}. Das Ziel ist erreichbar, aber nicht mit unveränderter Execution.`;
  }

  // ── SALES CYCLE WARNING ─────────────────────────────────────────
  let salesCycleWarning: string | null = null;
  if (m?.salesCycleWarning?.isSalesCycleTooLong) {
    const scw = m.salesCycleWarning;
    salesCycleWarning = `Sales Cycle Warnung: Ihr Sales Cycle beträgt ca. ${m.goalPath?.monthsRemainingApprox ?? "?"} Monate. Deals, die Sie heute starten, schließen möglicherweise erst im nächsten Jahr. Ca. ${scw.dealsUnreachableDueToSalesCycle} der noch benötigten Abschlüsse sind zeitlich kritisch.`;
  }

  return {
    hero,
    rootCause,
    rootCauseDetail,
    quickWins: quickWins.slice(0, 3),
    businessImpact,
    priorities: priorities.slice(0, 3),
    oneThing,
    risk,
    capacityMessage,
    salesCycleWarning,
  };
}
